import {
  estadoEquipo,
  estadoVisual,
  etiquetaEquipo,
  etiquetaMotivo,
  etiquetaRama,
  subtituloEquipo,
  fmtDuracion,
  hitoParteCreado,
  horaMadrid,
  parseTranscripcion,
  pasosLog,
  type PasoLog,
} from '@/lib/llamadas/presentacion';
import { useFichaCliente } from '@/hooks/useFichaCliente';
import { usePartesCliente } from '@/hooks/usePartesCliente';
import { useRevisionesEquipo } from '@/hooks/useRevisionesEquipo';
import { useLlamadaCompleta } from '@/hooks/useLlamadaCompleta';
import { useLlamadaEventos } from '@/hooks/useLlamadaEventos';
import { useParteLlamada } from '@/hooks/useParteLlamada';
import type { LlamadaRow } from '@/types/llamadas';
import { Icono } from '../Layout/iconos';
import { FichaCliente } from './FichaCliente';
import { HistorialPartes } from './HistorialPartes';
import { ParteOdoo } from './ParteOdoo';
import { Transcripcion } from './Transcripcion';

// Detalle de la llamada seleccionada (prototipo iter5): cabecera con teléfono
// + hora + duración, ficha del cliente, transcripción en burbujas y pie con
// motivo / resultado / resumen.

const ESTADO_TXT = {
  en_curso: 'En curso',
  procesando: 'Procesando análisis',
  resuelta: 'Resuelta por IA',
  escalada: 'Escalada',
} as const;

export function DetalleLlamada({ llamada }: { llamada: LlamadaRow }) {
  const { ficha, cargando } = useFichaCliente(llamada.id_cliente);
  const {
    partes,
    cargando: cargandoPartes,
    fallo: falloPartes,
  } = usePartesCliente(llamada.id_cliente);
  // v2: la transcripción no viaja en el feed; se carga aquí bajo demanda y se
  // refresca cuando la llamada seleccionada cambia (actualizado_en)
  const {
    completa,
    fresco: detalleFresco,
    cargando: cargandoConversacion,
    fallo: falloDetalle,
  } = useLlamadaCompleta(llamada.id, llamada.actualizado_en);
  // La puerta SOLO sale de un detalle fresco. Con `completa` a secas, el render
  // que sigue a un UPDATE de Realtime —y que pasa antes de que corra el efecto—
  // enseñaba la puerta de la versión anterior bajo la nueva, decidía el modo con
  // ella y la metía en la `key`. Ver el comentario largo de `useLlamadaCompleta`.
  const idEquipoLlamada = detalleFresco ? (completa?.id_equipo ?? null) : null;
  // Bitácora para la línea de tiempo. Mientras carga, `eventos` es [] y
  // `pasosLog` cae al camino reconstruido: contenido equivalente en vez de un
  // hueco, a cambio de que las horas del análisis puedan ajustarse al llegar.
  const { eventos } = useLlamadaEventos(llamada.call_id, llamada.actualizado_en);
  // El parte creado en Odoo, cuando la bitácora lo anota con el hito `parte_creado`.
  const parte = useParteLlamada(
    llamada.call_id,
    hitoParteCreado(eventos, llamada.call_id)?.id ?? null
  );
  // Las revisiones cuelgan de la PUERTA, y el id de la puerta viaja en el
  // detalle y no en el feed: por eso sale de `completa` (requisito descubierto
  // D2 de la feature base — con `llamada` sería siempre null y no se pediría
  // nunca ninguna revisión, con la pantalla entera pintada y sin fallar nada).
  const {
    revisiones,
    cargando: cargandoRevisiones,
    fallo: falloRevisiones,
  } = useRevisionesEquipo(idEquipoLlamada);
  const estado = estadoVisual(llamada);

  return (
    <div data-testid="detalle-llamada" className="flex min-h-0 flex-col overflow-y-auto p-4">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[var(--border-soft)] pb-3.5">
        <div>
          <div className="mono text-[17px] font-semibold text-[var(--navy)]">
            {/* Veracidad y no `??`: con `anonymous`, `_norm_tel` deja la cadena vacía
                y este hueco salía en blanco. La misma forma que en la fila y en la
                tabla del cuadro. */}
            {llamada.telefono_origen || 'Número oculto'}
          </div>
          <div className="mono mt-1 text-[12px] text-[var(--text-dim)]">
            {horaMadrid(llamada.inicio)}
            <span className="mx-1.5 opacity-50">·</span>
            {llamada.duracion_s === null ? '—' : `${fmtDuracion(llamada.duracion_s)} min`}
            {llamada.rama && (
              <>
                <span className="mx-1.5 opacity-50">·</span>
                {/* Por `etiquetaRama` y SIN `capitalize`, como los chips del cuadro y
                    la cabecera del informe impreso, que son los otros dos sitios que
                    escriben una rama. Con la rama en crudo y esa clase de CSS, el caso
                    NORMAL —`rama = 'aramatica'`— se leía «Aramatica»: el nombre del
                    cliente sin su acento, en cada llamada, mientras los otros dos
                    decían «Aramática». Y `capitalize` no arregla nada que la etiqueta
                    no traiga ya: a una rama desconocida le cambia la primera letra y la
                    deja distinta de como la dicen los demás. */}
                <span data-testid="rama-llamada">{etiquetaRama(llamada.rama)}</span>
              </>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10.5px] font-semibold uppercase tracking-wider text-[var(--text-mute)]">
            Motivo detectado
          </div>
          <div className="mt-1 flex items-center justify-end gap-1.5 text-[13px] font-semibold text-[var(--navy)]">
            <Icono nombre="sparkle" className="h-3.5 w-3.5 shrink-0 text-[var(--orange)]" />
            {/* Veracidad y no `!= null`: un motivo vacío es «sin motivo», y aquí eso
                es lo que dice el estado. */}
            {llamada.motivo ? etiquetaMotivo(llamada.motivo) : ESTADO_TXT[estado]}
          </div>
        </div>
      </div>

      <div className="mt-3.5">
        <FichaCliente ficha={ficha} cargando={cargando} telefonoOrigen={llamada.telefono_origen} />
        {/* Sin cliente identificado no se enseña el historial de nadie. */}
        {llamada.id_cliente && (
          <div className="mt-3">
            <HistorialPartes
              // La identidad del bloque es LLAMADA + CLIENTE + PUERTA, y las
              // tres hacen falta. Nació siendo el cliente (AD-P2-3: `ver todos`
              // viajaba a la siguiente llamada); con dos modos el estado local
              // es también «el resto está desplegado» y «veo todas las de esta
              // puerta», así que entre dos llamadas del mismo cliente no
              // remontaba (D10 incumplida); y con solo la llamada, un UPDATE de
              // Realtime que corrija el cliente o la puerta DENTRO de la misma
              // llamada dejaba el despliegue puesto sobre otro contexto (P2 de
              // la segunda vuelta del gate).
              // Ojo con el detalle: la `key` usa `completa?.id_equipo` A SECAS y
              // NO `idEquipoLlamada`, que está filtrado por `fresco`. Con el
              // filtrado, cualquier UPDATE de Realtime hacía E1 → null → E1 y
              // desmontaba el despliegue del operador aunque la puerta no
              // hubiera cambiado: arreglé una ventana rara y estropeé el caso
              // normal (regresión mía, hallazgo P2 de la cuarta vuelta). Sin
              // filtrar, el valor es estable y solo cambia cuando cambia la
              // puerta de verdad. El MODO sí exige frescura; la identidad, no.
              key={`${llamada.id}:${llamada.id_cliente ?? ''}:${completa?.id_equipo ?? ''}`}
              partes={partes}
              cargando={cargandoPartes}
              fallo={falloPartes}
              // `completa` y no `llamada`, por lo mismo que EquipoLlamada de abajo:
              // los campos del equipo no viajan en el feed. Con `llamada` esto era
              // siempre null y no se marcaba nunca ningún parte.
              idEquipoLlamada={idEquipoLlamada}
              referenciaEquipo={detalleFresco ? (completa?.equipo?.referencia ?? null) : null}
              revisiones={revisiones}
              cargandoRevisiones={cargandoRevisiones}
              falloRevisiones={falloRevisiones}
              // De qué puerta va la llamada es un dato con sus tres estados, y
              // el modo de la pantalla depende de él: no se decide hasta saberlo.
              puertaCargando={cargandoConversacion || !detalleFresco}
              puertaFallo={falloDetalle}
            />
          </div>
        )}
      </div>

      {/* `completa` y no `llamada`: los campos del equipo viajan en las columnas
          del DETALLE, no en las del feed, igual que la transcripción. Con la fila
          del feed el bloque no se pintaría nunca y no fallaría nada. */}
      <EquipoLlamada llamada={completa ?? llamada} />

      <div className="mt-4 min-h-0 flex-1">
        {cargandoConversacion && !completa ? (
          <p className="px-1 py-4 text-[12.5px] text-[var(--text-dim)]">Cargando conversación…</p>
        ) : (
          <Transcripcion mensajes={parseTranscripcion(completa?.transcripcion ?? null)} />
        )}
      </div>

      <div className="mt-4 space-y-2 border-t border-[var(--border-soft)] pt-3.5">
        {llamada.resumen && llamada.resumen !== llamada.resultado && (
          <p className="text-[12.5px] leading-relaxed text-[var(--text-2)]">{llamada.resumen}</p>
        )}
        {llamada.resultado && (
          <div className="flex items-center gap-2.5 text-[13px] font-medium text-[var(--navy)]">
            <span
              className="grid h-7 w-7 shrink-0 place-items-center rounded-lg"
              style={{
                background: estado === 'escalada' ? 'var(--status-esc-bg)' : 'var(--status-ok-bg)',
                color: estado === 'escalada' ? 'var(--status-esc-ink)' : 'var(--status-ok-ink)',
              }}
            >
              <Icono nombre={estado === 'escalada' ? 'escalate' : 'check'} className="h-4 w-4" />
            </span>
            {llamada.resultado}
          </div>
        )}
        <LogResolucion pasos={pasosLog(llamada, eventos)} />
        {parte && <ParteOdoo parte={parte} />}
      </div>
    </div>
  );
}

// Línea de tiempo de la llamada. Render puro: la lógica de qué pasos hay vive
// De qué puerta iba la llamada. Los tres estados se distinguen a la vista y no
// solo en el texto: una puerta de su ficha en Odoo y una frase de la
// conversación no valen lo mismo, y para el parte de la Fase 2 menos todavía.
// Cuando no hay dato no se pinta nada: las llamadas anteriores a esta feature no
// tienen el equipo porque nunca se pidió, no porque se intentara y fallara.
function EquipoLlamada({ llamada }: { llamada: LlamadaRow }) {
  const estado = estadoEquipo(llamada);
  if (estado === 'sin_dato') return null;

  const identificado = estado === 'identificado';
  return (
    <div
      data-testid="equipo-llamada"
      className="mt-2.5 flex items-start gap-2.5 rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2"
    >
      <span
        className="mt-px grid h-6 w-6 shrink-0 place-items-center rounded-lg"
        style={{
          background: identificado ? 'var(--status-ok-bg)' : 'var(--surface-3)',
          color: identificado ? 'var(--status-ok)' : 'var(--text-mute)',
        }}
      >
        {/* `box` y `doc` existen en ICONOS: `Icono` devuelve null en silencio
            con un nombre que no esté, así que un icono inventado no se pinta y
            nadie se entera. */}
        <Icono nombre={identificado ? 'box' : 'doc'} className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0">
        <p className="m-0 text-[12.5px] font-medium text-[var(--navy)]">
          {etiquetaEquipo(llamada)}
        </p>
        <p className="m-0 text-[11.5px] text-[var(--text-dim)]">{subtituloEquipo(llamada)}</p>
      </div>
    </div>
  );
}

// en `pasosLog` (src/lib/llamadas/presentacion.ts), que pinta desde la bitácora
// cuando la hay y reconstruye de la fila cuando no.
function LogResolucion({ pasos }: { pasos: PasoLog[] }) {
  if (pasos.length === 0) return null;

  return (
    <ol data-testid="log-resolucion" className="m-0 list-none space-y-1 pt-1.5 pl-0.5">
      {pasos.map((p, i) => (
        <li
          // hora+texto identifica el paso mejor que el índice: la lista crece
          // por Realtime y los pasos nuevos se intercalan por hora, no al final
          key={`${p.hora}-${p.texto}-${i}`}
          className={`flex items-baseline gap-2.5 text-[11.5px] ${
            p.pendiente ? 'text-[var(--text-mute)] italic' : 'text-[var(--text-dim)]'
          }`}
        >
          <span className="mono w-10 shrink-0 text-right">{p.hora}</span>
          <span
            className={`h-1.5 w-1.5 shrink-0 self-center rounded-full ${
              p.pendiente ? 'bg-[var(--border)]' : 'bg-[var(--text-mute)]'
            }`}
          />
          <span className="min-w-0">{p.texto}</span>
        </li>
      ))}
    </ol>
  );
}
